import {
  mergeMap,
  withLatestFrom,
  filter,
  takeUntil,
  delay,
} from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { of, zip, concat } from 'rxjs'
import * as actions from '../actions'
import pusher from '../../pusher/index'
import { EVENT_STATES } from '../../pusher/epics'
import { adVideoSelector } from '../../facebook-ads/selectors'
import { currentScopedBrand } from '../../brands/selectors'
import { rootCatchError } from '../../../lib/Utils'

export const subscribeToAdVideo = (action$, state$) => {
  return action$.pipe(
    ofType(actions.SUBSCRIBE_TO_AD_VIDEO),
    withLatestFrom(state$),
    mergeMap(([action]) => {
      const delay = action.payload.delay || 30000
      return of(
        pusher.actions.bindToChannelEvent({
          channelId: action.payload.channelId,
          eventId: action.payload.eventId,
          dispatchAction: actions.PUSHER_EVENT_AD_VIDEO_PROCESSING,
          delay,
        }),
        actions.pusherEventAdVideoProcessingTimerStart({
          channelId: action.payload.channelId,
          eventId: action.payload.eventId,
          delay,
        }),
      )
    }),
  )
}

export const unsubscribeFromAdVideoChannel = (action$, state$) => {
  return action$.pipe(
    ofType(actions.UNSUBSCRIBE_FROM_AD_VIDEO),
    withLatestFrom(state$),
    mergeMap(([action]) => {
      return of(
        pusher.actions.unbindFromChannelEvent({
          channelId: action.payload.channelId,
          eventId: action.payload.eventId,
        }),
        actions.pusherEventAdVideoProcessingTimerCancel(),
      )
    }),
  )
}

// Epic to unbind from channel when state is ready
export const handleVideoUploadComplete = (action$, state$) => {
  return action$.pipe(
    ofType(actions.PUSHER_EVENT_AD_VIDEO_PROCESSING),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      // TODO: add handling for this when coming from a GET request
      const { pusherEventStates } = pusher.selectors.selectPusherEventStates(
        state,
      )
      const { data, eventId, channelId } = action.payload
      const videoState = data && data.state
      const videoId = eventId
      const pusherSubscription = pusherEventStates[videoId]

      //Error code is empty string in non error cases
      if (videoState.error_code && videoState.error_code.length) {
        /*
        Possible error codes currently set by the backend. More may be added, hate to add that info here.
        Would love to see it as an enum on the erroc code value out of graphql in the future.
        ERROR_DECODING_FB_RESULT	Error decoding start_offset
        ERROR_DECODING_FB_RESULT	Error decoding end_offset
        ERROR_FACEBOOK	Error starting facebook upload
        ERROR_DECODING_FB_RESULT	Error decoding facebook upload response
        ERROR_READING_VIDEO	Error reading video chunk
        ERROR_MIME  Error creating form file for chunk. There are a number of equally obtuse error messages around the process of building up the necessary HTTP header for the upload.
        ERROR_REQUEST	Error creating the transfer request
        ERROR_FACEBOOK	Error uploading transfer chunk to Facebook
        ERROR_DECODING_FB_RESULT	Error decoding transfer response
        ERROR_FACEBOOK	Error finishing facebook upload
        */
        //Return and stop subscribing on error code.
        return of(
          actions.pusherEventAdVideoProcessingTimerCancel({
            channelId: action.payload.channelId,
            eventId: action.payload.eventId,
          }),
        )
      } else if (
        pusherSubscription === EVENT_STATES.OPEN &&
        videoState === 'ready'
      ) {
        const actionsToEmit = [
          pusher.actions.unbindFromChannelEvent({
            channelId,
            eventId: videoId,
          }),
          actions.pusherEventAdVideoProcessingTimerCancel({
            channelId: action.payload.channelId,
            eventId: action.payload.eventId,
          }),
        ]
        // Only fetch data if we received a pusher event
        if (action.type === actions.PUSHER_EVENT_AD_VIDEO_PROCESSING) {
          const adVideo = adVideoSelector(state)
          const { brandId } = (adVideo && adVideo[videoId]) || {}
          actionsToEmit.push(
            actions.getAdVideo({
              videoId,
              brandId,
            }),
          )
        }

        return actionsToEmit
      } else {
        return of(
          pusher.actions.staySubscribedToPusherChannel(),
          actions.pusherEventAdVideoProcessingTimerCancel({
            channelId: action.payload.channelId,
            eventId: action.payload.eventId,
          }),
          actions.pusherEventAdVideoProcessingTimerStart({
            channelId: action.payload.channelId,
            eventId: action.payload.eventId,
            delay: action.payload.delay,
          }),
        )
      }
    }),
  )
}

export const pusherTimer = (action$, state$) => {
  return action$.pipe(
    ofType(actions.PUSHER_EVENT_AD_VIDEO_PROCESSING_TIMER_START),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { channelId, eventId } = action.payload
      const { brand } = currentScopedBrand(state)
      return zip(
        of(
          actions.getAdVideo({
            videoId: action.payload.eventId,
            brandId: brand.id,
          }),
        ),
        of(
          pusher.actions.unbindFromChannelEvent({
            channelId: action.payload.channelId,
            eventId: action.payload.eventId,
          }),
        ),
      ).pipe(
        //NOTE: We allow the delay to be injected here for testing.
        //We also optionally allow a scheduler to be included. This schedulers allows
        //Jest and other tests to simulate the passage of time.
        delay(
          action.payload.delay || 30000,
          action.payload.scheduler || undefined,
        ),
        takeUntil(
          action$.pipe(
            filter((action) => {
              return (
                action.type ===
                  actions.PUSHER_EVENT_AD_VIDEO_PROCESSING_TIMER_CANCEL &&
                action.payload.eventId === eventId &&
                action.payload.channelId === channelId
              )
            }),
          ),
        ),
        mergeMap((actions) => {
          return concat(actions)
        }),
      )
    }),
    rootCatchError(actions.PUSHER_EVENT_AD_VIDEO_PROCESSING_TIMER_ERROR),
  )
}
